/**
 * Astryx 테마 선택 상태 (개발용 톤앤매너 전환).
 *
 * 배포 빌드는 항상 DEFAULT_THEME_KEY(y2k) 로 고정된다 — 저장값은 dev 에서만 읽는다.
 * localStorage 에 저장해 새로고침 후에도 비교 중인 테마가 유지된다.
 * 첫 페인트 깜빡임은 index.html 의 인라인 스크립트가 같은 키를 읽어 막는다.
 */
import { create } from "zustand";
import {
  DEFAULT_THEME,
  DEFAULT_THEME_KEY,
  THEME_SWITCHER_ENABLED,
  findThemeEntry,
  type AstryxTheme,
  type ThemeMode,
} from "../theme/themes";

export const THEME_STORAGE_KEY = "peeka.theme";
export const THEME_MODE_STORAGE_KEY = "peeka.themeMode";

function readStored(key: string): string | null {
  if (!THEME_SWITCHER_ENABLED) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode 등 — 저장 실패는 무시 */
  }
}

const initialKey = readStored(THEME_STORAGE_KEY) ?? DEFAULT_THEME_KEY;
const initialEntry = findThemeEntry(initialKey);
const storedMode = readStored(THEME_MODE_STORAGE_KEY);
const initialMode: ThemeMode = initialEntry.darkOnly
  ? "dark"
  : storedMode === "light" || storedMode === "dark"
    ? storedMode
    : "dark";

interface ThemeStore {
  themeKey: string;
  /** 로드 완료된 테마 객체. 전환 중에는 직전 테마를 유지해 깜빡임을 막는다. */
  theme: AstryxTheme;
  mode: ThemeMode;
  selectTheme: (key: string) => Promise<void>;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeStore>()((set, get) => ({
  // 초기값은 항상 정적 y2k — 저장된 테마가 다르면 아래 hydrate 에서 교체된다.
  themeKey: initialEntry.key,
  theme: DEFAULT_THEME,
  mode: initialMode,

  selectTheme: async (key) => {
    const entry = findThemeEntry(key);
    const theme = await entry.load();
    set({
      themeKey: entry.key,
      theme,
      // darkOnly 테마로 가면 라이트에서 깨지므로 dark 로 스냅한다.
      mode: entry.darkOnly ? "dark" : get().mode,
    });
    write(THEME_STORAGE_KEY, entry.key);
  },

  setMode: (mode) => {
    if (findThemeEntry(get().themeKey).darkOnly) return;
    set({ mode });
    write(THEME_MODE_STORAGE_KEY, mode);
  },
}));

/**
 * 저장된 테마가 기본값이 아니면 dynamic import 로 교체 (dev 전용).
 * main.tsx 부팅 시 1회 호출.
 */
export function hydrateStoredTheme(): void {
  if (!THEME_SWITCHER_ENABLED) return;
  if (initialEntry.key === DEFAULT_THEME_KEY) return;
  void useThemeStore.getState().selectTheme(initialEntry.key);
}
